import React, { useState } from 'react';
import styled from 'astroturf/react';
import { formatDistanceToNowStrict } from 'date-fns';

import type { Props } from 'js/logged_out_bucketing_bar/types';

// The panel is the only part that touches cookies and the full test list. It
// stays out of the main bundle, which every visitor loads.
const BucketingPanel = React.lazy(
  () =>
    import(
      /* webpackChunkName: "logged-out-bucketing-bar" */ 'js/logged_out_bucketing_bar/BucketingPanel'
    ),
);

const Bar = styled.div`
  align-items: center;
  background: #000;
  color: #fff;
  display: flex;
  flex-wrap: wrap;
  font-family: 'Roboto Mono', monospace;
  font-size: 12px;
  gap: 10px;
  line-height: 1.4;
  padding: 8px 12px;
`;

const ErrorBar = styled(Bar)`
  background: #7a1414;
`;

// A column so the panel stacks ABOVE the bar in normal flow. The panel used to
// be absolutely positioned at a fixed offset, which assumed the bar was one line
// tall; on a phone the bar wraps to three and the panel covered the title and the
// forced-arm count.
const Dock = styled.div`
  bottom: 0;
  display: flex;
  flex-direction: column;
  left: 0;
  position: fixed;
  right: 0;
  z-index: 2147483000;
`;

const Title = styled.span`
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const Detail = styled.span`
  color: #bbb;
`;

const Toggle = styled.button`
  background: transparent;
  border: 1px solid #fff;
  border-radius: 18px;
  color: #fff;
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  padding: 4px 12px;

  &:hover {
    background: #fff;
    color: #000;
  }
`;

const expiresIn = (expiresAt: string | null): string | null => {
  if (expiresAt == null) return null;

  const parsed = new Date(expiresAt);
  if (Number.isNaN(parsed.getTime())) return null;

  return formatDistanceToNowStrict(parsed);
};

const LoggedOutBucketingBar: React.FC<Props> = ({
  expiresAt,
  staleOverrideNames,
  status,
  tests,
}) => {
  const [expanded, setExpanded] = useState(false);

  if (status === 'invalid_token') {
    return (
      <Dock>
        <ErrorBar>
          <Title>Logged-out test override</Title>
          <span>
            That link is expired or invalid. Ask an admin for a fresh one from
            the admin bar.
          </span>
        </ErrorBar>
      </Dock>
    );
  }

  // Only overrides the server will actually apply. A stale one is reported
  // beside this count, never inside it.
  const forcedCount = tests.filter((test) => test.override != null).length;
  const remaining = expiresIn(expiresAt);

  return (
    <Dock>
      {expanded && (
        <React.Suspense fallback={null}>
          <BucketingPanel
            staleOverrideNames={staleOverrideNames}
            tests={tests}
          />
        </React.Suspense>
      )}
      <Bar>
        <Title>Logged-out test override</Title>
        <Detail>
          {forcedCount === 0 ? 'no arm forced' : `${forcedCount} arm(s) forced`}
        </Detail>
        {staleOverrideNames.length > 0 && (
          <Detail>· {staleOverrideNames.length} on a finished test</Detail>
        )}
        {remaining != null && <Detail>· expires in {remaining}</Detail>}
        <Toggle onClick={() => setExpanded(!expanded)}>
          {expanded ? 'close' : 'pick an arm'}
        </Toggle>
      </Bar>
    </Dock>
  );
};

export default LoggedOutBucketingBar;
